import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {
  GetMinimalProductBulkResponse,
  ImagesSection_Image,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {ImageType} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {BadgeList} from '../../../features/BadgeList/BadgeList';
import {useBadgesList} from '../../../features/BadgeList/useBadgesList';
import {WishlistButton} from '../../../features/ProductTile/WishlistButton';
import {useCampaignAndSaleBadgeTextByProduct} from '../../../shared-deprecated/useCampaignAndSaleBadgeTextByProduct';
import {useGetBadgeListKeys} from '../utils/useGetBadgeListKeys';
import {ProductTileImageSlider} from './ProductTileImageSlider/ProductTileImageSlider';

export interface IProductImageSliderViewProps {
  selectedSizeId?: number;
  redirectOnClick?: boolean;
  showThumbnails?: boolean;
  showAddToWishlistButton?: boolean;
  replaceHistory?: boolean;
  previewImage?: ImageType;
  isInMiniADPDialog: boolean;
}

interface IProductTileImage {
  src: string;
  alt?: string;
  padded?: boolean;
}

export const ProductTileImageSliderView = ({
  images,
  badges,
  brand,
  product,
  selectedSizeId,
  redirectOnClick = false,
  showThumbnails = false,
  showAddToWishlistButton = true,
  replaceHistory,
  previewImage,
  isInMiniADPDialog,
}: GetMinimalProductBulkResponse['imagesSection'] &
  IProductImageSliderViewProps) => {
  const {t} = useTranslation();
  const {isActive: isPriceOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  const {sale, campaignSale} = useCampaignAndSaleBadgeTextByProduct({
    product,
    selectedSizeId,
    badges,
  });

  const badgesList = useBadgesList({
    badges,
    campaignSale,
    sale: isPriceOverhaulV2Active ? t('Sale').toUpperCase() : sale,
    showPriceOverhaulV2SaleLabel: isPriceOverhaulV2Active,
  });
  const {badgesListKeys} = useGetBadgeListKeys({
    isInMiniADPDialog,
    productId: product.id,
  });

  const productTileImages: Array<IProductTileImage> = images.map(
    (imageSectionImage: ImagesSection_Image): IProductTileImage => {
      return {
        src: imageSectionImage.image.src,
        alt: imageSectionImage.image.description,
        padded: imageSectionImage.image.isEnclosed,
      };
    },
  );

  const BadgesListComponent = (
    <StyledBadgeList badgesList={badgesList} increaseRightSideBorderRadius />
  );

  const WishlistButtonComponent = showAddToWishlistButton ? (
    <WishlistButton
      brand={brand}
      product={product}
      streamPosition={0}
      badgesListKeys={badgesListKeys}
    />
  ) : undefined;

  return (
    <Wrapper>
      <ProductTileImageSlider
        images={productTileImages}
        slideCount={1}
        redirectPath={redirectOnClick ? product.path : undefined}
        showThumbnails={showThumbnails}
        BadgesListComponent={BadgesListComponent}
        WishlistButtonComponent={WishlistButtonComponent}
        replaceHistory={replaceHistory}
        previewImage={previewImage}
      />
    </Wrapper>
  );
};

const StyledBadgeList = styled(BadgeList)`
  border-radius: 0 4px 4px 0;
  bottom: 16px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
